/**
 * Formato compartido para el panel "Uso de IA" (AdminDashboardPage) y la
 * página de reportes (/admin/uso-ia) — nombres de módulo y helpers de
 * formato de tokens/minutos/caracteres/costo, para que ambos lugares no
 * diverjan.
 */

// Nombres legibles por módulo — deben reflejar los `modulo` que escribe
// `registrarUsoIA` (functions/src/utils/registrarUsoIA.ts, llamadas de texto)
// y `usage_tracking.py` (livekit-agent-shared, llamadas de voz).
export const MODULO_LABELS: Record<string, string> = {
  triage: 'Triage (chat)',
  notas_medicas: 'Notas médicas (transcripción)',
  asistente_medico: 'Asistente médico',
  analisis_sentimiento: 'Análisis de sentimiento',
  analisis_expediente: 'Análisis de expediente',
  resumen_paciente: 'Resumen para el paciente',
  prueba_triage: 'Prueba de triage (admin)',
  asistente_configuracion: 'Asistente de configuración',
  reporte_problema: 'Reporte de problemas',
  transcripcion_llamada: 'Transcripción de la consulta (voz)',
  triage_voz: 'Triage por voz (paciente)',
  asistente_configuracion_voz: 'Asistente de configuración (voz)',
  voz_escritorio: 'Voz de escritorio',
};

export function moduloLabel(modulo: string): string {
  return MODULO_LABELS[modulo] || modulo;
}

// Tipo de unidad que factura cada fila — 'llm' (tokens) para Gemini/GPT de
// texto, 'stt' (minutos de audio) y 'tts' (caracteres) para los agentes de
// voz. Los docs escritos antes de agregar esta distinción no tienen `tipo`
// → se tratan como 'llm' (siempre fueron tokens de Gemini).
export type TipoUsoIA = 'llm' | 'stt' | 'tts';

export function tipoDe(tipo: string | undefined): TipoUsoIA {
  return tipo === 'stt' || tipo === 'tts' ? tipo : 'llm';
}

/** Nombre corto de un `modelo` que en voz es un label de plugin de LiveKit
 *  Agents (ej. "livekit.plugins.deepgram.stt.STT") — se muestra solo la
 *  última parte útil (proveedor + tipo), ej. "deepgram.STT". */
export function nombreModelo(modelo: string): string {
  const partes = modelo.split('.');
  return partes.length >= 2 ? partes.slice(-2).join('.') : modelo;
}

export function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(Math.round(n));
}

export function fmtCosto(n: number): string {
  return `$${n.toFixed(n < 1 ? 4 : 2)}`;
}

/** Costo que puede no existir: sin dato muestra «—», nunca $0. */
export function fmtCostoOpc(n: number | null | undefined): string {
  return n == null || !Number.isFinite(n) ? '—' : fmtCosto(n);
}

/** Número con separador de miles y, si hace falta, hasta `dec` decimales. */
export function fmtNumero(n: number, dec = 1): string {
  return n.toLocaleString('es-PA', { maximumFractionDigits: dec });
}

// ── Monitoreo de servicios (video, voz, infraestructura…) ─────────────────
export const CATEGORIA_LABELS: Record<string, string> = {
  video: 'Videollamadas',
  voz: 'Voz',
  infra: 'Infraestructura de Google',
  biometrico: 'Registro biométrico',
  fijo: 'Costos fijos',
};

export function categoriaLabel(categoria: string): string {
  return CATEGORIA_LABELS[categoria] || categoria;
}

export const FUENTE_COSTO_LABELS: Record<string, string> = {
  estimado: 'Estimado',
  facturado: 'Facturado',
  fijo: 'Fijo',
};

/** Cantidad de un servicio con su unidad (ej. «1,234 min», «$12.50»). */
export function fmtCantidadServicio(cantidad: number, unidad: string): string {
  if (unidad === 'USD') return fmtCosto(cantidad);
  const dec = Math.abs(cantidad) >= 100 ? 0 : 2;
  return `${fmtNumero(cantidad, dec)} ${unidad}`;
}

/** Minutos de audio, con 1 decimal (ej. "12.4 min"). */
export function fmtMinutos(segundos: number): string {
  return `${(segundos / 60).toFixed(1)} min`;
}

/** Caracteres de TTS, con el mismo formato compacto que fmtTokens (K/M). */
export function fmtCaracteres(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(Math.round(n));
}

/** Formatea la "cantidad" de una fila según su tipo (tokens/minutos/caracteres). */
export function fmtCantidad(tipo: TipoUsoIA, d: { tokensTotal?: number; audioSegundos?: number; caracteres?: number }): string {
  if (tipo === 'stt') return fmtMinutos(d.audioSegundos ?? 0);
  if (tipo === 'tts') return fmtCaracteres(d.caracteres ?? 0);
  return fmtTokens(d.tokensTotal ?? 0);
}
